import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth"
import { Shell } from "@/components/Shell"
import {
  getMealsByDate,
  listRecipes,
  listWeight,
  type MealsByDate,
  type RecipeSummary,
  type WeightEntry,
} from "@/lib/api"
import { useWeightUnit, toDisplay, formatDelta } from "@/lib/units"
import { localToday } from "@/lib/dates"

/**
 * Dashboard — daily glance. Today's macro totals against targets +
 * latest weight + 7d trend arrow + last 5 meals.
 */
export function DashboardPage() {
  const auth = useAuth()
  const { unit } = useWeightUnit()
  const [meals, setMeals] = useState<MealsByDate | null>(null)
  const [weight, setWeight] = useState<WeightEntry[]>([])
  // Saved-cookbook count drives both the persistent quick-action surface
  // ("cookbook · N dishes") and the first-meal value-prop. null = not yet
  // loaded; a falsy 0 is meaningful and rendered as "0 dishes".
  const [cookbookCount, setCookbookCount] = useState<number | null>(null)
  // One-time welcome line after first-time profile save — flag set in Profile.tsx.
  // Read+clear once at mount; never set again (so React StrictMode double-mount
  // in dev is benign — prod single-mount works as intended).
  const [welcome] = useState<boolean>(() => {
    const v = sessionStorage.getItem("fittrack:welcome") === "1"
    if (v) sessionStorage.removeItem("fittrack:welcome")
    return v
  })

  useEffect(() => {
    if (auth.status !== "authed") return
    // Local date, not UTC — see bug-bash M11.
    const today = localToday()
    void getMealsByDate(today).then(setMeals).catch(() => {})
    void listWeight(14).then(setWeight).catch(() => {})
    // Saved-only — auto-saved log entries don't count as "your cookbook".
    void listRecipes("saved")
      .then((rs: RecipeSummary[]) => setCookbookCount(rs.length))
      .catch(() => setCookbookCount(0))
  }, [auth.status])

  if (auth.status !== "authed") return null
  const t = auth.me.targets
  const totals = meals?.totals
  const name = auth.me.user.display_name || auth.me.user.email.split("@")[0]
  const latest = weight[0]
  const avg7 = weight.length
    ? weight.slice(0, 7).reduce((a, e) => a + e.weight_kg, 0) / Math.min(7, weight.length)
    : null
  const delta = latest && avg7 != null ? latest.weight_kg - avg7 : null

  return (
    <Shell
      currentPath="/"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/ingredients", label: "Ingredients" },
        { href: "/recipes", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="eyebrow mb-6 animate-ink-in">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </p>

        <h1
          className="font-display text-display tracking-tightest mb-3 animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          {welcome ? "All set, " : "Welcome back, "}
          <span className="italic text-moss">{name}</span>.
        </h1>

        {/* Welcome-after-first-save: the project receipt, with a soft pivot
            into the value-prop hero below it. Kept compact — the big
            framing happens in WhyFitTrack. */}
        {welcome && t && (
          <div className="mt-6 mb-2 px-5 py-4 border-l-2 border-moss bg-moss/[0.04] max-w-2xl animate-fade-up">
            <p className="font-sans text-sm leading-relaxed">
              Profile saved. Daily target{" "}
              <span className="font-mono text-foreground">
                {t.calories.toLocaleString()} kcal
              </span>
              {auth.me.recommendation && auth.me.recommendation.weeks_to_goal > 0 && (
                <>
                  {" "}· goal in roughly{" "}
                  <span className="font-mono text-moss">
                    {Math.round(auth.me.recommendation.weeks_to_goal)} weeks
                  </span>
                </>
              )}
              {auth.me.recommendation && auth.me.recommendation.weeks_to_goal === 0 && (
                <> · you're at or below your healthy range. 🎯</>
              )}
              .
            </p>
          </div>
        )}

        {/* Value-prop hero — shown to first-time users AND to anyone whose
            cookbook is still empty (≤2 saved dishes). Once the user has a
            real library, the dashboard goes lean and gets out of the way. */}
        {t && (cookbookCount == null || cookbookCount <= 2) && (
          <WhyFitTrack cookbookCount={cookbookCount ?? 0} />
        )}

        {!t ? (
          <div className="mt-10">
            <p className="font-sans text-base text-muted-foreground mb-6 max-w-xl">
              First step — set up your profile so we can compute your daily targets.
            </p>
            <a
              href="/profile"
              className="inline-flex items-center gap-3 bg-foreground text-background px-6 py-3.5 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors"
            >
              Set up profile <span aria-hidden>→</span>
            </a>
          </div>
        ) : (
          <>
            <p className="font-sans text-base text-muted-foreground max-w-xl leading-relaxed mb-12">
              Today's running totals against your targets — and your weight trend.
            </p>

            {/* Macros: today actual vs target */}
            <section className="mb-12">
              <div className="flex items-baseline justify-between mb-5 gap-4 flex-wrap">
                <p className="eyebrow">today's macros</p>
                <div className="flex items-baseline gap-5">
                  <a
                    href="/recipes/new"
                    className="eyebrow text-moss hover:text-foreground transition-colors"
                  >
                    + build a dish
                  </a>
                  <a href="/today" className="eyebrow text-muted-foreground hover:text-foreground">
                    log a meal →
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border card-surface overflow-hidden">
                <MacroBlock label="calories" current={totals?.calories ?? 0} target={t.calories} unit="kcal" />
                <MacroBlock label="protein" current={totals?.protein_g ?? 0} target={t.protein_g} unit="g" />
                <MacroBlock label="fiber" current={totals?.fiber_g ?? 0} target={t.fiber_g} unit="g" />
                <MacroBlock label="carbs" current={totals?.carbs_g ?? 0} target={t.carbs_g} unit="g" />
                <MacroBlock label="sugar (cap)" current={totals?.sugar_g ?? 0} target={t.sugar_g} unit="g" warn />
              </div>
            </section>

            {/* Weight + Cookbook + last meals — three columns on lg, stacked on sm */}
            <div className="grid lg:grid-cols-[1fr_1fr_1.4fr] gap-12">
              <section>
                <div className="flex items-baseline justify-between mb-5">
                  <p className="eyebrow">weight</p>
                  <a href="/weight" className="eyebrow text-muted-foreground hover:text-foreground">
                    log →
                  </a>
                </div>
                {latest ? (
                  <div className="card-surface px-6 sm:px-8 py-6">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-5xl num tracking-tightest">
                        {toDisplay(latest.weight_kg, unit).toFixed(1)}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">{unit}</span>
                    </div>
                    <p className="font-mono text-xs text-muted-foreground mt-1">{latest.date}</p>
                    {delta != null && (
                      <p className={`font-mono text-xs mt-4 ${delta < 0 ? "text-moss" : delta > 0 ? "text-persimmon" : "text-muted-foreground"}`}>
                        {delta > 0 ? "▲" : delta < 0 ? "▼" : "—"}{" "}
                        {formatDelta(Math.abs(delta), unit).replace(/^\+/, "")} vs 7d avg
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="card-surface px-6 py-6">
                    <p className="font-sans text-sm text-muted-foreground mb-4">No weight entries yet.</p>
                    <a href="/weight" className="inline-block bg-foreground text-background px-4 py-2 rounded font-sans text-xs hover:bg-moss transition-colors">
                      Log first weight →
                    </a>
                  </div>
                )}
              </section>

              <section>
                <div className="flex items-baseline justify-between mb-5">
                  <p className="eyebrow">cookbook</p>
                  <a href="/recipes/new" className="eyebrow text-moss hover:text-foreground transition-colors">
                    + build →
                  </a>
                </div>
                <a href="/recipes" className="card-surface px-6 sm:px-8 py-6 block hover:border-foreground/40 transition-colors group">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-5xl num tracking-tightest">
                      {cookbookCount ?? "—"}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {cookbookCount === 1 ? "dish" : "dishes"}
                    </span>
                  </div>
                  <p className="font-mono text-xs text-muted-foreground mt-1">
                    {cookbookCount && cookbookCount > 0
                      ? "your library · tap to browse"
                      : "build dishes once, log them in one tap"}
                  </p>
                </a>
              </section>

              <section>
                <div className="flex items-baseline justify-between mb-5">
                  <p className="eyebrow">today's log</p>
                  <a href="/today" className="eyebrow text-muted-foreground hover:text-foreground">all →</a>
                </div>
                {!meals?.meals.length ? (
                  <p className="font-sans text-sm text-muted-foreground">Nothing logged yet.</p>
                ) : (
                  <ul className="divide-y divide-border border-t border-b border-border">
                    {meals.meals.slice(0, 5).map((m) => (
                      <li key={m.id} className="py-3 flex items-baseline justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-sans text-sm truncate">{m.item_name}</p>
                          <p className="font-mono text-xs text-muted-foreground">
                            {m.quantity_g}g · {m.meal_type}
                          </p>
                        </div>
                        <span className="font-mono text-xs tabular-nums whitespace-nowrap">
                          {m.calories.toFixed(0)} kcal
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </Shell>
  )
}

/**
 * WhyFitTrack — three-card editorial explainer. Shown to first-time users
 * AND to anyone with a sparse cookbook (≤2 dishes). Numbered like the
 * Profile sections so the visual vocabulary carries — large display
 * numerals, italic moss accent on the key noun, mono caption underneath.
 *
 * The third card adapts: for true first-timers (count=0) it nudges them
 * to build their first dish; once they've started (1-2) it celebrates
 * what they have and points at the next one.
 */
function WhyFitTrack({ cookbookCount }: { cookbookCount: number }) {
  return (
    <section
      className="mt-10 mb-14 animate-fade-up"
      style={{ animationDelay: "240ms" }}
      aria-label="Why FitTrack is different"
    >
      <p className="eyebrow mb-5">why fittrack · the short version</p>
      <div className="grid md:grid-cols-3 gap-px bg-border card-surface overflow-hidden">
        <Pillar
          n="01"
          headline={<>One dish, <span className="italic text-moss">one tap</span>.</>}
          body="Masala dosa with sambar and chutney is one entry — not four. Group what you cook the way you eat it."
        />
        <Pillar
          n="02"
          headline={<>Built <span className="italic text-moss">your way</span>.</>}
          body="Paste a recipe URL, snap a photo of a cookbook page, or type it from memory. The app parses it into your private library."
        />
        <Pillar
          n="03"
          headline={
            cookbookCount === 0 ? (
              <>A <span className="italic text-moss">plan</span>, not a number.</>
            ) : (
              <>You've started — <span className="italic text-moss">keep going</span>.</>
            )
          }
          body={
            cookbookCount === 0
              ? "Set a target weight, pick a timeline or a deficit, and watch how the days add up. Daily numbers serve the plan, not the other way around."
              : `${cookbookCount} ${cookbookCount === 1 ? "dish" : "dishes"} in your library so far. Add a few more and your daily logging becomes single taps.`
          }
          cta={{ href: "/recipes/new", label: "Build a dish →" }}
        />
      </div>
    </section>
  )
}

function Pillar({
  n,
  headline,
  body,
  cta,
}: {
  n: string
  headline: React.ReactNode
  body: string
  cta?: { href: string; label: string }
}) {
  return (
    <article className="bg-surface px-6 sm:px-8 py-7 flex flex-col gap-3 min-w-0">
      <p className="font-mono text-[11px] tracking-wider text-muted-foreground">{n}</p>
      <h3 className="font-display text-2xl sm:text-3xl tracking-tightest leading-tight">
        {headline}
      </h3>
      <p className="font-sans text-sm text-muted-foreground leading-relaxed">
        {body}
      </p>
      {cta && (
        <a
          href={cta.href}
          className="eyebrow text-moss hover:text-foreground transition-colors mt-2"
        >
          {cta.label}
        </a>
      )}
    </article>
  )
}

function MacroBlock({
  label, current, target, unit, warn = false,
}: {
  label: string; current: number; target: number; unit: string; warn?: boolean
}) {
  const over = current > target
  const numColor = warn && over ? "text-persimmon" : "text-foreground"
  const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0
  return (
    <div className="bg-surface px-6 sm:px-7 py-6 sm:py-7 flex flex-col gap-3 min-w-0">
      <p className="eyebrow whitespace-nowrap overflow-hidden text-ellipsis">{label}</p>
      <div className="flex items-baseline gap-2 min-w-0">
        <span className={`font-display text-4xl sm:text-5xl tracking-tightest num ${numColor}`}>
          {current.toFixed(0)}
        </span>
        <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
          /{target.toLocaleString()} {unit}
        </span>
      </div>
      <div className="h-px bg-border relative overflow-visible">
        <div
          className={`absolute top-0 left-0 h-px transition-all duration-500 ease-out ${over ? "bg-persimmon" : "bg-moss"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
